import { z } from 'zod'
import { applyDemoCheckIn } from '@/lib/demo/checkIn'

import {
  applyShowAction,
  showClearance,
  showRecordSchema,
  type ShowContext,
  type ShowRecord,
} from './showModeModel'

export const visitSchema = z.object({
  id: z.string().min(1),
  customerId: z.string().min(1),
  customerName: z.string(),
  deviceId: z.string(),
  deviceName: z.string(),
  date: z.iso.date(),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
  kind: z.string(),
  kindLabel: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string().nullable(),
  creditState: z.string().nullable(),
  creditStateLabel: z.string().nullable(),
})
export const workspaceSchema = z
  .object({
    version: z.literal(1),
    locationId: z.string().min(1),
    revision: z.number().int().nonnegative(),
    validityMonths: z.number().int().min(1).max(36),
    appointments: z.array(visitSchema),
    credits: z.record(
      z.string(),
      z.object({
        purchased: z.number().int().nonnegative(),
        consumed: z.number().int().nonnegative(),
      }),
    ),
    records: z.record(z.string(), z.unknown()),
  })
  .superRefine((ws, ctx) => {
    if (
      new Set(ws.appointments.map((a) => a.id)).size !== ws.appointments.length ||
      Object.entries(ws.credits).some(
        ([id, c]) =>
          c.consumed +
            ws.appointments.filter(
              (a) => a.customerId === id && a.status === 'booked' && a.creditState === 'reserved',
            ).length >
          c.purchased,
      )
    )
      ctx.addIssue({ code: 'custom', message: 'Widersprüchliches Terminkontingent.' })
  })
export type ShowVisit = z.infer<typeof visitSchema>
export type ShowWorkspace = z.infer<typeof workspaceSchema>

export function studioDay(instant: string, timezone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(instant))
}

/** Clamp month-end and evaluate calendar days in the studio, not the device zone. */
export function validThrough(signedAt: string, months: number, timezone: string) {
  const [year, month, day] = studioDay(signedAt, timezone).split('-').map(Number)
  const end = new Date(Date.UTC(year, month - 1 + months + 1, 0))
  return `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(Math.min(day, end.getUTCDate())).padStart(2, '0')}`
}

export function creditBalance(ws: ShowWorkspace, customerId: string) {
  const { purchased, consumed } = ws.credits[customerId] ?? { purchased: 0, consumed: 0 }
  const pending = ws.appointments.filter(
    (a) => a.customerId === customerId && a.status === 'booked',
  )
  const reserved = pending.filter((a) => a.creditState === 'reserved').length
  return {
    purchased,
    consumed,
    reserved,
    available: purchased - consumed - reserved,
    openContingent: pending.filter((a) => a.creditState === 'open').length,
  }
}

export function eligibility(
  ws: ShowWorkspace,
  record: ShowRecord,
  appointmentId: string,
  context: ShowContext,
  now: string,
) {
  workspaceSchema.parse(ws)
  showRecordSchema.parse(record)
  const reasons: string[] = []
  const appointment = ws.appointments.find(
    (a) => a.id === appointmentId && a.customerId === context.customerId,
  )
  const credits = creditBalance(ws, context.customerId)
  const latest = record.documents.at(-1)
  const expires = latest ? validThrough(latest.signedAt, ws.validityMonths, context.timezone) : null
  if (
    ws.locationId !== context.locationId ||
    record.scope.locationId !== context.locationId ||
    record.scope.customerId !== context.customerId
  )
    reasons.push(
      'Der Kunden- oder Standortkontext stimmt nicht überein. Öffnen Sie den Termin erneut.',
    )
  if (!appointment)
    reasons.push('Kein passender Termin ausgewählt. Bitte wählen oder buchen Sie einen Termin.')
  else if (appointment.status !== 'booked')
    reasons.push(
      'Dieser Termin ist bereits eingecheckt, abgeschlossen oder abgesagt. Wählen Sie einen gebuchten Termin.',
    )
  if (record.treatments.some((t) => !t.completedAt))
    reasons.push('Eine Behandlung läuft bereits. Bitte schließen Sie diese zuerst ab.')
  if (!latest)
    reasons.push(
      'Die unterschriebene Anamnese fehlt. Bitte erfassen und unterschreiben Sie die Anamnese.',
    )
  else {
    if (latest.signedAt > now)
      reasons.push(
        'Das Unterschriftsdatum liegt in der Zukunft. Prüfen Sie die Gerätezeit und die Anamnese.',
      )
    if (expires && studioDay(now, context.timezone) > expires)
      reasons.push(
        `Die Anamnese ist älter als ${ws.validityMonths} Monate. Bitte erfassen Sie eine erneute Anamnese.`,
      )
    if (showClearance(record) !== 'ready')
      reasons.push(
        'Die Anamnese benötigt eine Rücksprache. Lassen Sie die Freigabe durch die Filialleitung dokumentieren.',
      )
  }
  if (credits.purchased - credits.consumed < 1)
    reasons.push(
      'Der Kunde hat kein verfügbares Kontingent. Bitte lassen Sie vor der Behandlung ein Kontingent gutschreiben.',
    )
  else if (appointment?.creditState !== 'reserved')
    reasons.push(
      'Für diesen Termin ist kein Kontingent reserviert. Bitte klären Sie das Kontingent für diesen Termin.',
    )
  return {
    allowed: reasons.length === 0,
    reasons,
    expires,
    credits,
    appointment,
    recordRevision: record.revision,
    workspaceRevision: ws.revision,
  }
}

/** One persisted workspace update contains treatment, check-in and credit consumption. */
export function startVisit(
  ws: ShowWorkspace,
  record: ShowRecord,
  appointmentId: string,
  context: ShowContext,
  now: string,
  id: string,
) {
  const result = eligibility(ws, record, appointmentId, context, now)
  if (!result.allowed) throw new Error(result.reasons.join('\n'))
  const next = workspaceSchema.parse(ws)
  const nextRecord = applyShowAction(record, { type: 'start' }, context, now, id)
  nextRecord.treatments.at(-1)!.appointmentId = appointmentId
  applyDemoCheckIn(next, appointmentId, context.customerId)
  next.records[context.customerId] = showRecordSchema.parse(nextRecord)
  return workspaceSchema.parse(next)
}
